import { useMutation } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { throwFromResponse } from '@/features/users/lib/apiErrorMessage';
import type {
  ManualOrderCreateRequest,
  ManualOrderCreateResponse,
} from '../types/manualOrder';

/**
 * ID55 수기주문 생성 API. (be-manual-order 계약 — order-service.)
 * 게이트웨이 `/admin/v1/orders/**` rewrite 재사용. v2 envelope.
 * Idempotency-Key(더블클릭/네트워크 재시도 중복 주문 방지). 생성 경로가 OpenAPI
 * 스펙에 미반영이라 overseas/shippingFee 정책과 동일하게 adminFetch 로 직접 호출.
 */
const PATH = '/admin/v1/orders/manual';

interface ManualOrderEnvelope {
  success: boolean;
  data?: ManualOrderCreateResponse | null;
}

export const useManualOrderCreateMutation = () =>
  useMutation({
    mutationFn: async (
      body: ManualOrderCreateRequest,
    ): Promise<ManualOrderCreateResponse> => {
      const res = await adminFetch(PATH, {
        method: 'POST',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('POST', PATH, body),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        // BE 가 한글 에러 메시지(envelope.error.message)를 우선 노출하도록 위임.
        await throwFromResponse(res, '수기주문 등록에 실패했습니다.');
      }
      const env = (await res.json()) as ManualOrderEnvelope;
      if (!env.data) throw new Error('수기주문 응답이 비어 있습니다.');
      return env.data;
    },
  });
